import React, { useEffect, useState } from 'react';
import api from '../api';
import './AdminVideoManager.css';

function AdminVideoManager({ token }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const [videos, setVideos] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const requestWithFallback = async (method, path, data = undefined) => {
    const candidates = [
      { baseURL: undefined },
      { baseURL: 'http://localhost:5000' },
      { baseURL: 'http://127.0.0.1:5000' },
    ];

    let lastError;
    for (const candidate of candidates) {
      try {
        const config = {
          method,
          url: path,
          headers,
        };
        if (candidate.baseURL) config.baseURL = candidate.baseURL;
        if (data !== undefined) config.data = data;
        return await api.request(config);
      } catch (err) {
        lastError = err;
        const status = err?.response?.status;
        // Retry on route/base mismatch or network failure; stop on auth/validation errors.
        if (status && status !== 404) break;
      }
    }
    throw lastError;
  };

  const loadVideos = async (targetPage = page) => {
    try {
      setLoading(true);
      const res = await requestWithFallback('get', `/api/videos/admin/list?page=${targetPage}&limit=6`);
      if (res && res.data && res.data.success) {
        setVideos(res.data.data || []);
        setPage(res.data.pagination?.page || targetPage);
        setTotalPages(res.data.pagination?.pages || 1);
      }
    } catch (err) {
      console.error('Failed to load admin videos:', err?.response?.status, err?.response?.data || err.message || err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadVideos(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submitVideo = async (e) => {
    e.preventDefault();
    if (!title.trim() || !youtubeUrl.trim()) {
      alert('Title और YouTube URL required हैं');
      return;
    }

    try {
      setSaving(true);
      const res = await requestWithFallback(
        'post',
        '/api/videos/admin',
        {
          title: title.trim(),
          description: description.trim(),
          youtubeUrl: youtubeUrl.trim(),
        }
      );

      if (res?.data?.success) {
        setTitle('');
        setDescription('');
        setYoutubeUrl('');
        await loadVideos(1);
        alert('Video added successfully');
      } else {
        alert(res?.data?.message || 'Failed to add video');
      }
    } catch (err) {
      alert(err?.response?.data?.message || err.message || 'Failed to add video');
    } finally {
      setSaving(false);
    }
  };

  const deleteVideo = async (id) => {
    if (!window.confirm('Is video ko delete karna hai?')) return;
    try {
      const res = await requestWithFallback('delete', `/api/videos/admin/${id}`);
      if (res?.data?.success) {
        const nextPage = videos.length === 1 && page > 1 ? page - 1 : page;
        await loadVideos(nextPage);
      } else {
        alert(res?.data?.message || 'Failed to delete video');
      }
    } catch (err) {
      alert(err?.response?.data?.message || err.message || 'Failed to delete video');
    }
  };

  return (
    <section className="admin-video-manager">
      <div className="avm-head">
        <h3>📺 Homepage Video Section Manager</h3>
        <p>Title, description और YouTube लिंक डालते ही homepage sidebar में embed हो जाएगा।</p>
      </div>

      <form className="avm-form" onSubmit={submitVideo}>
        <div className="avm-field">
          <label htmlFor="videoTitle">Video Title</label>
          <input
            id="videoTitle"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Video title"
            required
          />
        </div>

        <div className="avm-field">
          <label htmlFor="videoUrl">YouTube Link</label>
          <input
            id="videoUrl"
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            required
          />
        </div>

        <div className="avm-field">
          <label htmlFor="videoDesc">Description</label>
          <textarea
            id="videoDesc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description"
            rows={3}
          />
        </div>

        <button className="avm-submit" type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Add Video'}
        </button>
      </form>

      <div className="avm-list-wrap">
        <div className="avm-list-head">
          <h4>Latest Added Videos</h4>
          <button type="button" onClick={() => loadVideos(page)} className="avm-refresh">Refresh</button>
        </div>

        {loading ? (
          <p className="avm-empty">Loading videos...</p>
        ) : videos.length === 0 ? (
          <p className="avm-empty">No videos added yet.</p>
        ) : (
          <div className="avm-list">
            {videos.map((video) => (
              <article className="avm-item" key={video._id}>
                <img src={video.thumbnailUrl} alt={video.title} loading="lazy" />
                <div className="avm-item-content">
                  <h5>{video.title}</h5>
                  {video.description && <p>{video.description}</p>}
                  <a href={video.youtubeUrl} target="_blank" rel="noreferrer">Open YouTube</a>
                </div>
                <button type="button" className="avm-delete" onClick={() => deleteVideo(video._id)}>Delete</button>
              </article>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="avm-pagination">
            <button type="button" disabled={page <= 1} onClick={() => loadVideos(page - 1)}>Prev</button>
            <span>Page {page} / {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => loadVideos(page + 1)}>Next</button>
          </div>
        )}
      </div>
    </section>
  );
}

export default AdminVideoManager;
